
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import NavigationLayout from "components/layout/NavigationLayout";
import Image from "next/image";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import styles from "pages/store/brands/[bid].module.scss";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Axios } from "api";
import utilStyles from "styles/utils.module.scss";
import TopTabBar, { NewTopTabBar } from "components/TopTabBar";
import Loading from "components/Loading";
import Paginator from "components/Paginator";
import ProductGrid from "components/ProductGrid";
import ProductListCard from "components/cards/ProductListCard";
import classNames from "classnames";
import { productScrapContextRef } from "context/ProductScrapContext";
import { captureException, captureMessage } from "@sentry/nextjs";
import { getReferralInfo } from "common/referral";
import Head from "next/head";
import { generateSEOLink, SITE_URL, specialOffersRoute } from "common/const";
import Link from "next/link";
import ArrowRightIcon from "images/icons/arrow_right_757575.svg";
import { checkBatchimEnding, getLocalStorageCountryCode, numberWithComma } from "common/utils";
import { KEY_PROMOTION_ENDS_AT } from "pages/store/special-offers";
import Layout from "components/layout/layout";
import TopBar from "components/layout/TopBar/TopBar";
import NewBadgeIcon from "images/icons/new_badge.svg";
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
const KEY_RANK = 'rank';
const KEY_SELL_COUNT = 'sell_count';
const KEY_RECENT = 'recent';
const KEY_LOW_PRICE = 'low_price';
const KEY_HIGH_PRICE = 'high_price';
const PAGE_SIZE = 40;
const ProductList = (props) => {
    const { t } = useTranslation('store-brands-bid');
    const [productList, setProductList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const router = useRouter();
    const order = router.query.order;
    const brandId = props.brandId || router.query?.bid;
    const page = router.query.page ? (parseInt(router.query.page) || 1) : 1;
    const isLarge = useMediaQuery(`(min-width:${utilStyles.breakpointDesktop})`);
    const referralInfo = getReferralInfo();
    useEffect(async () => {
        if (!router.isReady) {
            return;
        }
        if (router.isReady && !order) {
            router.replace({ query: Object.assign({}, router.query, { order: KEY_SELL_COUNT }) }, undefined, { shallow: true, scroll: true });
            return;
        }
        setTotalCount(0);
        try {
            setIsLoading(true);
            const res = await Axios.get(`v1/store/brand-products/${brandId}`, {
                params: {
                    page_num: page,
                    page_size: PAGE_SIZE,
                    order: order
                }
            });
            if (res.status < 400) {
                setProductList(res.data.products);
                setTotalCount(res.data.count);
                productScrapContextRef.current.addProductIds(res.data.scrapped_pids);
            }
        }
        catch (e) {
            setProductList([]);
            setTotalCount(0);
            console.log(e);
        }
        finally {
            setIsLoading(false);
        }
    }, [brandId, order, page]);
    return (<>
            <div className={styles.productSection}/>
            <div className={styles.sectionTitleMargin}>
                <span className={classNames(styles.topProductsTitle)}>{t('ProductList.allProducts')}</span>
                <span className={styles.totalCount}>
                    {t('ProductList.totalCount', { count: numberWithComma(totalCount) })}
                </span>
            </div>
            <NewTopTabBar smallText activeRoute={order} tabs={[
            {
                route: KEY_SELL_COUNT,
                title: t('ProductList.sortBySales'),
                onClick: () => {
                    router.replace({ query: Object.assign({}, router.query, { order: KEY_SELL_COUNT }) }, undefined, {
                        shallow: true,
                        scroll: false
                    });
                }
            },
            {
                route: KEY_RECENT,
                title: t('ProductList.sortByRecent'),
                onClick: () => {
                    router.replace({ query: Object.assign({}, router.query, { order: KEY_RECENT }) }, undefined, {
                        shallow: true,
                        scroll: false
                    });
                }
            },
        ]}/>
            <div className={styles.productGridMargin}/>
            <div className={styles.productGridPadding}>
                {productList.length === 0 && isLoading ?
            <Loading style={{ marginTop: 150, marginBottom: 300 }}/>
            :
                (<>
                                <ProductGrid products={productList} referralInfo={referralInfo} vertical/>
                                <div className={styles.paginatorMargin}/>
                                <Paginator isLoading={isLoading} currentPage={page} size={isLarge ? 10 : 5} onChangeCurrentPage={(e) => {
                        router.replace({ query: Object.assign({}, router.query, { page: e }) }, undefined, {
                            shallow: true,
                            scroll: false
                        });
                    }} totalCount={totalCount} countPerPage={PAGE_SIZE}/>
                            </>)}
            </div>
        </>);
};
const SpecialOfferSection = ({ brandId, brandInfo }) => {
    const { t } = useTranslation('store-brands-bid');
    const isMobile = useMediaQuery(`(max-width:${utilStyles.breakpointMobile})`);
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const router = useRouter();
    const bid = brandId || router.query?.bid;
    useEffect(() => {
        getSpecialOffers();
    }, []);
    const referralInfo = getReferralInfo();
    const getSpecialOffers = async () => {
        try {
            setIsLoading(true);
            setProducts([]);
            const res = await Axios.get('v1/store/special-offer-products/', { params: { order: KEY_PROMOTION_ENDS_AT, brand_id: bid } });
            console.log(res);
            if (res.status < 400) {
                setProducts(res.data.products);
                productScrapContextRef.current.updateByProductIdsAndScrappedProductIds(res.data.products.map(e => e.id), res.data['scrapped_pids']);
            }
            else {
                setError(res.data.display_message || t('SpecialOfferSection.loadError'));
            }
        }
        catch (e) {
            setError(t('SpecialOfferSection.loadError'));
        }
        finally {
            setIsLoading(false);
        }
    };
    return (products.length > 0 &&
        <>
            {isMobile && <div className={styles.mobileBorder}/>}
            <div className={styles.topProductsSection}>
                <div className={styles.specialProductsTitle}>{t('SpecialOfferSection.hemekoSpecialPrice')}</div>
                <div className={styles.productsWrapper}>
                    <div className={styles.horizontalScrollPaddingLeft}/>
                    {products.map((e, index) => {
                return (<div className={styles.cardWrapper} key={index}>
                                    <ProductListCard product={e} showPromotionDaysLeft miniScrapIcon={true}/>
                                </div>);
            })}
                    <div className={styles.horizontalScrollPaddingRight}/>
                </div>
            </div>
        </>);
};
export const Brand = (props) => {
    const { t } = useTranslation('store-brands-bid');
    const isMobile = useMediaQuery(`(max-width:${utilStyles.breakpointMobile})`);
    const isLarge = useMediaQuery(`(min-width:${utilStyles.breakpointDesktop})`);
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);
    const [brandInfo, setBrandInfo] = useState(null);
    const [error, setError] = useState(false);
    const bid = props.brandId || router.query?.bid;
    useEffect(() => {
        if (!router.isReady)
            return;
        const query = router.query;
        if (!query.page || !query.order) {
            router.replace({ query: { bid: bid, page: query.page || 1, order: query.order || KEY_RANK } }, undefined, {
                shallow: true,
                scroll: true
            });
        }
    }, [router.isReady, router.query]);
    useEffect(() => {
        window.scrollTo(0, 0);
        if (!bid)
            return;
        getBrandPageInfo(bid);
    }, [router.isReady, bid]);
    const countryCode = getLocalStorageCountryCode() ?? 'KR';
    const getBrandPageInfo = async (brand_id) => {
        try {
            setIsLoading(true);
            setError(false);
            const loggingData = {};
            const res = await Axios.get(`v1/store/brands/${brand_id}`);
            if (res.status < 400) {
                console.log('getBrandPageInfo', res.data);
                setBrandInfo(res.data);
                productScrapContextRef.current.addProductIds(res.data.scrapped_pids);
                loggingData['success'] = true;
            }
            else {
                setError(true);
                alert(res.data.display_message || '브랜드 정보를 받아오지 못했습니다. 잠시 후 다시 시도해주세요.');
                if (!res.data.display_message) {
                    captureMessage(JSON.stringify(res.data));
                }
                loggingData['success'] = false;
            }
            typeof mixpanel !== 'undefined' && mixpanel.track('ViewBrandDetail', loggingData);
        }
        catch (e) {
            setError(true);
            alert('브랜드 정보를 받아오지 못했습니다. 잠시 후 다시 시도해주세요.');
            captureException(e);
        }
        finally {
            setIsLoading(false);
        }
    };
    const referralInfo = getReferralInfo();
    const metaTitle = brandInfo ? t('Brand.metaTitleTemplate', { brandName: brandInfo.name }) : '';
    return (<>
            <Head>
                {generateSEOLink(`/store/brands/${bid || 0}`, router.locale)}
                {brandInfo &&
            <>
                        <meta name="description" content={brandInfo.description} key="description"/>
                        <title>{metaTitle}</title>
                        <meta property="og:type" content="website" key="ogType"/>
                        <meta property="og:title" content={metaTitle} key="ogTitle"/>
                        <meta property="og:description" content={brandInfo.description} key="ogDescription"/>
                        <meta property="og:image" content={brandInfo.logo_image_url} key="ogImage"/>
                    </>}
            </Head>
            <div className={classNames(styles.container, utilStyles.pageContainer)}>
                {!error && isLoading &&
            <Loading style={{ marginTop: 60 }}/>}
                {error &&
            (<div className={styles.errorContainer}>
                            <span className={styles.errorText}>
                                {t('Brand.loadBrandInfoError')}
                            </span>
                        </div>)}
                {!isLoading && !error && brandInfo &&
            <div className={styles.brandInfoContainer}>
                        <div className={styles.bannerWrapper}>
                            <div className={styles.bannerImage}>
                                <Image unoptimized src={isMobile ? brandInfo.banner_mobile_image_url || brandInfo.banner_image_url : brandInfo.banner_image_url} alt={brandInfo.name} layout="fill" objectFit="cover"/>
                            </div>
                            <div className={styles.logoImage}>
                                <Image unoptimized src={brandInfo.logo_image_url ?? ''} alt={brandInfo.name} layout="fill" objectFit="cover"/>
                            </div>
                        </div>

                        <div className={styles.brandDescriptionWrapper}>
                            <div className={classNames(utilStyles.alignItemsCenter, utilStyles.flexRow)}>
                                <span className={styles.brandPageName}>{brandInfo.name}</span>
                                {brandInfo.is_new &&
                    <div className={styles.newBadge}>
                                        <NewBadgeIcon width="7" height="7" viewBox="0 0 7 7"/>
                                    </div>}
                            </div>
                            {countryCode === 'KR' && brandInfo.english_name &&
                    <span className={styles.brandPageEnglishName}>{brandInfo.english_name}</span>}
                            <span className={styles.brandDescription}>{brandInfo.description}</span>
                        </div>
                        <SpecialOfferSection brandInfo={brandInfo} brandId={props.brandId}/>
                        <div className={styles.mobileBorder}/>
                    </div>}
                {!isLoading && !error &&
            <ProductList brandId={props.brandId}/>}
            </div>
        </>);
};
const BrandWrapper = (props) => {
    return (<Layout topBar={<TopBar isBack search pouch/>}>
            <Brand {...props}/>
        </Layout>);
};
Brand.getLayout = NavigationLayout;
export default BrandWrapper;

    async function __Next_Translate__getServerSideProps__1966fcd683a__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/store/brands/[bid]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1966fcd683a__ as getServerSideProps }
  